'use client';

import { createContext, useContext, type ReactNode } from 'react';

export interface NavbarUser {
  id?: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  provider?: 'email' | 'google';
}

export interface NavbarAccountLink {
  href: string;
  label: string;
  icon?: 'book' | 'brain' | 'heart' | 'gear' | 'user';
  badge?: string;
  badgeColor?: 'green' | 'magenta' | 'brand';
}

export interface NavbarContextValue {
  user: NavbarUser | null;
  cartItemCount: number;
  cartLoading: boolean;
  favoritesCount: number;
  favoritesPulseKey?: number;
  favoritesUrl?: string;
  hideUserActions: boolean;
  hideLogin: boolean;
  loginUrl: string;
  registerUrl: string;
  homeUrl: string;
  classroomUrl: string;
  classroomBadge?: string;
  accountUrl: string;
  accountLinks: NavbarAccountLink[];
  onCartClick?: () => void | Promise<void>;
  onLogin?: () => void;
  onLogout?: () => void | Promise<void>;
  onNavClick?: (item: { href: string; label: string; url: string }) => void;
}

const NavbarContext = createContext<NavbarContextValue | null>(null);

export const useNavbarContext = (): NavbarContextValue => {
  const ctx = useContext(NavbarContext);
  if (!ctx) {
    throw new Error('useNavbarContext must be used inside NavbarProvider');
  }
  return ctx;
};

export const NavbarProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: NavbarContextValue;
}) => <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>;
